import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ShieldAlert, AlertTriangle, Ban, CheckCircle, Loader2, Send } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };

function GapCard({ sub, api, onResolved }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [dq, setDq] = useState(sub.dq);

  const fields = [];
  const seen = new Set();
  (dq.issues || []).forEach(i => (i.fix_fields || []).forEach(f => {
    if (!seen.has(f.key)) { seen.add(f.key); fields.push(f); }
  }));

  const submit = async () => {
    const filled = Object.fromEntries(Object.entries(values).filter(([, v]) => String(v).trim() !== ''));
    if (!Object.keys(filled).length) { toast.error('Fill in at least one field'); return; }
    setSaving(true);
    try {
      const r = await api.post(`/submissions/${sub.submission_id}/fix-data`, { fields: filled });
      setDq(r.data.dq);
      setValues({});
      if (r.data.dq.clean) {
        toast.success(`${sub.business_name} — all data gaps resolved, quote back with the underwriter`);
        onResolved(sub.submission_id);
      } else {
        toast.info(`Updated — ${r.data.dq.issues.length} item${r.data.dq.issues.length === 1 ? '' : 's'} still outstanding`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the corrections');
    } finally { setSaving(false); }
  };

  if (dq.clean) {
    return (
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(29,158,117,0.3)', background: 'rgba(29,158,117,0.04)' }}
        data-testid={`gap-card-resolved-${sub.submission_id}`}>
        <p className="text-sm flex items-center gap-2" style={{ color: C.green }}>
          <CheckCircle className="w-4 h-4" /> <b>{sub.business_name}</b> — resolved. The submission was automatically re-checked and it's back in the underwriting queue.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(226,75,74,0.2)' }} data-testid={`gap-card-${sub.submission_id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold" style={{ color: C.navy }}>{sub.business_name}</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }}>
          {sub.insurance_class || sub.class}
        </span>
        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm ml-auto"
          style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>
          {dq.issues.length} gap{dq.issues.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="space-y-1 mb-3">
        {dq.issues.map(i => {
          const color = i.severity === 'high' ? C.red : C.amber;
          const Icon = i.kind === 'inconsistent' ? Ban : AlertTriangle;
          return (
            <div key={i.code} className="flex items-start gap-2 text-xs" data-testid={`gap-issue-${sub.submission_id}-${i.code.toLowerCase()}`}>
              <Icon className="w-3 h-3 mt-0.5 shrink-0" style={{ color }} />
              <span style={{ color: C.navy }}>
                <span className="font-bold text-[9px] uppercase mr-1.5 px-1 py-0.5 rounded-sm" style={{ background: `${color}1A`, color }}>{i.kind}</span>
                {i.detail}
              </span>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>{f.label}</label>
            <input type={f.type} value={values[f.key] || ''}
              onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
              data-testid={`gap-input-${sub.submission_id}-${f.key}`}
              className="mt-0.5 w-full px-2.5 py-1.5 text-xs rounded-sm border focus:outline-none"
              style={{ borderColor: 'rgba(136,150,168,0.35)', color: C.navy }} />
          </div>
        ))}
      </div>
      <button onClick={submit} disabled={saving} data-testid={`gap-submit-${sub.submission_id}`}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
        style={{ background: C.navy }}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Submit corrections
      </button>
    </Card>
  );
}

export default function BrokerDataGapsPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolved, setResolved] = useState([]);

  useEffect(() => {
    api.get('/broker/data-gaps').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  const onResolved = useCallback((id) => setResolved(prev => [...prev, id]), []);

  if (loading) return <div className="space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;

  const subs = data?.submissions || [];
  const open = subs.length - resolved.length;

  return (
    <div className="space-y-4" data-testid="broker-data-gaps-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: 'Georgia, serif' }}>Data Gaps</h1>
        <p className="text-sm mt-1" style={{ color: C.slate }}>
          Automatic data checks flagged missing or inconsistent details on these quotes. Fix them here and each quote goes straight back to the underwriter — no emails needed.
        </p>
      </div>
      {subs.length === 0 ? (
        <Card className="p-6 rounded-md text-center" style={{ border: '1px solid rgba(29,158,117,0.25)' }} data-testid="data-gaps-empty">
          <CheckCircle className="w-8 h-8 mx-auto mb-2" style={{ color: C.green }} />
          <p className="text-sm font-semibold" style={{ color: C.navy }}>All clear</p>
          <p className="text-xs mt-1" style={{ color: C.slate }}>None of your queued quotes have outstanding data gaps.</p>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md" data-testid="data-gaps-banner"
            style={{ background: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.2)' }}>
            <ShieldAlert className="w-4 h-4 shrink-0" style={{ color: C.red }} />
            <p className="text-xs" style={{ color: C.navy }}>
              <strong>{open}</strong> quote{open === 1 ? '' : 's'} waiting on corrected data — resolving these speeds up your terms.
            </p>
          </div>
          <div className="space-y-3">
            {subs.map(s => <GapCard key={s.submission_id} sub={s} api={api} onResolved={onResolved} />)}
          </div>
        </>
      )}
    </div>
  );
}
